/**
 * Admin-only warning: orientation assignments whose multiplayer session never
 * arrived. The trainee only sees "still being provisioned" and has no way to
 * act on it; the cause is almost always the org's onboarding-session token
 * (expired, or it lost its mp Writer grant), which only an admin can fix in
 * Settings. The module keeps retrying, so fixing the token clears the banner
 * on its own within half an hour, with no re-assign.
 *
 * Reads the admin-scoped `cohort_progress` / `cohort_assignment_progress`
 * views, so it is empty (and hidden) for non-admins by construction too.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables as minetwinTables } from '@sdk/minetwin';
import { toMillis } from '../lib/toMillis';
import { stuckAssignments } from '../lib/stuckProvisioning';

/** Re-evaluate once a minute: an assignment turns stuck by the clock alone,
 *  with no table change to trigger a render. */
const TICK_MS = 60_000;

export function StuckProvisioningBanner({ onOpenSettings }: { onOpenSettings: () => void }) {
  const [assignments] = useTable('minetwin', minetwinTables.cohort_progress);
  const [pointers] = useTable('minetwin', minetwinTables.cohort_assignment_progress);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(t);
  }, []);

  const stuck = useMemo(() => {
    const sessionIdByAssignment = new Map(pointers.map((p) => [p.assignmentId, p.sessionId]));
    const rows = assignments.map((a) => ({
      assignmentId: a.assignmentId,
      status: a.status,
      assignedAtMs: toMillis(a.assignedAt),
      email: a.email,
    }));
    return stuckAssignments(rows, sessionIdByAssignment, now);
  }, [assignments, pointers, now]);

  if (stuck.length === 0) return null;

  const oldestMin = Math.floor((now - Math.min(...stuck.map((s) => s.assignedAtMs))) / 60_000);
  const who = stuck.slice(0, 3).map((s) => s.email).join(', ') + (stuck.length > 3 ? ', …' : '');

  return (
    <div
      role="alert"
      style={{
        margin: '10px 16px 0',
        padding: '10px 14px',
        border: '1px solid var(--bad-line, var(--bad))',
        borderRadius: 'var(--r, 8px)',
        background: 'var(--panel)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontSize: 12.5,
      }}
    >
      <div style={{ display: 'grid', gap: 2 }}>
        <b style={{ color: 'var(--bad)' }}>
          {stuck.length} orientation{stuck.length === 1 ? '' : 's'} stuck without a multiplayer
          session (oldest {oldestMin} min)
        </b>
        <span style={{ color: 'var(--muted)' }}>
          {who}. Trainees see "still being provisioned" and cannot start. The usual cause is the
          onboarding-session token: expired, or no longer allowed to create sessions. Save a valid
          token and these recover on their own within 30 minutes.
        </span>
      </div>
      <button className="btn" style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }} onClick={onOpenSettings}>
        Open Settings
      </button>
    </div>
  );
}
